// CallCommandPanel — pre-call briefing + post-call automation.
//
// Three tabs:
//   · Pre-Call    → GET /api/call-command/prep     (briefing, UW flags, confirmation SMS)
//   · Post-Call   → POST /api/call-command/log     (thank-you + email + notes + reminders)
//   · Outreach    → POST /api/self-gen/preview     (GSB spintax templates, N variants)
//
// Health-sector safe: PHI displayed here comes from loadClientFabric
// which enforces the demo-mode / entitlement gate on the API side.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  try {
    const t = readPersistedJwtDirect();
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

function Card({ title, right, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      {title && (
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">{title}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function UwVerdictBadge({ verdict }) {
  const map = {
    clear:           { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'CLEAR — likely approved' },
    requires_review: { bg: 'bg-amber-100',   fg: 'text-amber-800',   label: 'REQUIRES REVIEW' },
    hard_decline:    { bg: 'bg-red-100',     fg: 'text-red-800',     label: 'HARD DECLINE' },
  };
  const c = map[verdict] || map.requires_review;
  return <span className={`inline-block px-2 py-1 rounded font-bold text-[11px] ${c.bg} ${c.fg}`}>{c.label}</span>;
}

function PreCall({ leadId, appointmentAt }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [checked, setChecked] = React.useState({});
  const [prepState, setPrepState] = React.useState(null);   // { complete, completed_at, updated_at }
  const [savingPrep, setSavingPrep] = React.useState(false);
  const [sending, setSending] = React.useState(null);
  const [sentFlags, setSentFlags] = React.useState({});

  // Debounced save — collapses rapid checkbox toggles into a single
  // POST. Ref persists across renders without triggering re-renders.
  const saveTimer = React.useRef(null);
  const persistPatch = React.useCallback(async (patch) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingPrep(true);
      try {
        const headers = await bearer();
        const r = await fetch('/api/call-command/prep-state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ lead_id: leadId, patch }),
        });
        const j = await r.json();
        if (r.ok && j.state) {
          setPrepState(j.state);
          setChecked(j.state.checked || {});
        }
      } catch { /* soft-fail — local state persists until next toggle */ }
      finally { setSavingPrep(false); }
    }, 400);
  }, [leadId]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      const headers = await bearer();
      // Fire prep briefing + prep-state load in parallel.
      const [briefRes, stateRes] = await Promise.all([
        fetch('/api/call-command/prep', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ lead_id: leadId, appointment_at: appointmentAt }),
        }).catch(e => ({ ok: false, _err: e })),
        fetch('/api/call-command/prep-state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ lead_id: leadId }),
        }).catch(e => ({ ok: false, _err: e })),
      ]);
      if (!mounted) return;
      try {
        if (briefRes.ok) {
          const j = await briefRes.json();
          setData(j);
        } else {
          const j = briefRes._err ? { error: briefRes._err.message } : await briefRes.json().catch(() => ({}));
          setError(j.error || `HTTP ${briefRes.status || 'ERR'}`);
        }
      } catch (e) { setError(e.message); }
      try {
        if (stateRes.ok) {
          const j = await stateRes.json();
          if (j.state) {
            setPrepState(j.state);
            setChecked(j.state.checked || {});
          }
        }
      } catch { /* checklist state fails soft */ }
      setLoading(false);
    })();
    return () => { mounted = false; if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [leadId, appointmentAt]);

  const sendConfirmation = async (kind, text) => {
    if (!data?.lead?.phone) return;
    setSending(kind);
    try {
      const headers = await bearer();
      const r = await fetch('/api/messages/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ lead_id: leadId, to: data.lead.phone, body: text }),
      });
      if (r.ok) setSentFlags(f => ({ ...f, [kind]: true }));
    } catch { /* soft-fail */ }
    finally { setSending(null); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading pre-call briefing…</div>;
  if (error)   return <div className="p-6 text-sm text-red-700">Failed to load: {error}</div>;
  if (!data)   return null;

  const s = data.profile_snapshot || {};
  const uw = data.underwriting || {};
  const cf = data.client_fabric || {};

  const CHECKLIST = [
    { key: 'reviewed_notes',    label: 'Reviewed CRM notes + client profile' },
    { key: 'quotes_built',      label: 'Built quotes in ACA / UHC / Med Mutual / Allstate' },
    { key: 'tabs_open',         label: 'Carrier tabs + CRM + notes + script open' },
    { key: 'meds_reviewed',     label: 'Reviewed medications + provider networks' },
    { key: 'uw_reviewed',       label: 'Reviewed underwriting drill-downs' },
    { key: 'screen_share',      label: 'Screen share ready' },
    { key: 'confirmations_sent',label: 'Confirmation texts sent' },
    { key: 'leader_ready',      label: 'Leader identified + available' },
    { key: 'mindset',           label: 'Mindset check — confident + in control' },
  ];

  return (
    <div className="space-y-3">
      {/* Snapshot header */}
      <Card title="Pre-call briefing">
        <div className="text-lg font-bold text-slate-900">{s.name || data.lead?.name || 'Unnamed lead'}</div>
        <div className="text-xs text-slate-600 mt-0.5">
          {[s.age != null && `${s.age}yo`, s.state, s.city, s.household_size != null && `hh ${s.household_size}`,
            s.household_income_band, s.smoker === true ? 'smoker' : s.smoker === false ? 'non-smoker' : null]
            .filter(Boolean).join(' · ') || 'Demographics incomplete — verify on call'}
        </div>
      </Card>

      {/* Underwriting */}
      <Card title="Underwriting assessment" right={<UwVerdictBadge verdict={uw.overall_verdict}/>}>
        {uw.hard_declines?.length > 0 && (
          <div className="mb-2">
            <div className="text-[10px] font-bold text-red-800 uppercase tracking-wider mb-1">Hard-decline flags</div>
            <ul className="space-y-0.5">
              {uw.hard_declines.map((d, i) => <li key={i} className="text-xs text-red-800">· {d.reason}</li>)}
            </ul>
          </div>
        )}
        {uw.warnings?.length > 0 && (
          <div className="mb-2">
            <div className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Requires review</div>
            <ul className="space-y-0.5">
              {uw.warnings.map((w, i) => <li key={i} className="text-xs text-amber-800">· {w.reason}</li>)}
            </ul>
          </div>
        )}
        {uw.matched?.length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Conditions on file ({uw.matched.length})</div>
            <ul className="space-y-0.5">
              {uw.matched.map((m, i) => (
                <li key={i} className="text-[11px] text-slate-700">
                  · <span className="font-semibold">{m.label}</span>
                  {m.requirements?.length > 0 && (
                    <span className="text-slate-500"> — {m.requirements.slice(0, 2).join('; ')}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(!uw.hard_declines?.length && !uw.warnings?.length && !uw.matched?.length) && (
          <div className="text-xs text-slate-500 italic">No conditions on file — verify on call.</div>
        )}
      </Card>

      {/* Client fabric */}
      {(cf.primary_doctor || cf.preferred_pharmacy || cf.best_time_to_call || cf.current_prescriptions?.length > 0) && (
        <Card title="Client fabric">
          {cf.primary_doctor       && <div className="text-xs text-slate-700"><span className="font-semibold">Doctor:</span> {cf.primary_doctor}</div>}
          {cf.preferred_pharmacy   && <div className="text-xs text-slate-700"><span className="font-semibold">Pharmacy:</span> {cf.preferred_pharmacy}</div>}
          {cf.best_time_to_call    && <div className="text-xs text-slate-700"><span className="font-semibold">Best time:</span> {cf.best_time_to_call}</div>}
          {cf.current_prescriptions?.length > 0 && (
            <div className="text-xs text-slate-700 mt-1">
              <span className="font-semibold">Rx:</span> {cf.current_prescriptions.slice(0, 5).map(r => `${r?.name || '?'}${r?.dosage ? ` ${r.dosage}` : ''}`).join(', ')}
            </div>
          )}
          {cf.personal_notes && (
            <div className="text-[11px] text-slate-600 mt-2 italic">Personal: {String(cf.personal_notes).slice(0, 240)}</div>
          )}
        </Card>
      )}

      {/* Carriers */}
      <Card title="Carrier tabs to open">
        <ul className="space-y-1.5">
          {(data.carriers || []).map((c) => (
            <li key={c.key} className="text-[11px]">
              <div className="flex justify-between gap-2">
                <span className="font-semibold text-slate-800">{c.label}</span>
                <span className="text-slate-500 font-mono">{c.demographics}</span>
              </div>
              <div className="text-slate-500 italic text-[10px]">{c.notes}</div>
            </li>
          ))}
        </ul>
      </Card>

      {/* Confirmation SMS */}
      <Card title="Confirmation SMS ready to send">
        {['morning_of', 'two_min_before'].map((k) => {
          const text = data.confirmation_templates?.[k];
          if (!text) return null;
          const label = k === 'morning_of' ? 'Morning of' : '2 min before';
          const sent  = !!sentFlags[k];
          return (
            <div key={k} className="mb-2">
              <div className="flex justify-between items-baseline mb-0.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
                <button
                  onClick={() => !sent && sendConfirmation(k, text)}
                  disabled={sent || sending === k || !data.lead?.phone}
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                    sent ? 'bg-emerald-100 text-emerald-800' :
                    sending === k ? 'bg-slate-200 text-slate-500' :
                    'bg-slate-900 text-white hover:bg-slate-700'
                  }`}
                >
                  {sent ? 'Sent ✓' : sending === k ? 'Sending…' : 'Send'}
                </button>
              </div>
              <div className="text-xs text-slate-800 bg-slate-50 rounded p-2">{text}</div>
            </div>
          );
        })}
      </Card>

      {/* Checklist — persisted via /api/call-command/prep-state */}
      <Card
        title="Pre-call checklist"
        right={
          prepState?.complete
            ? <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded uppercase tracking-wider">Prep complete ✓</span>
            : savingPrep
              ? <span className="text-[10px] text-slate-500 italic">Saving…</span>
              : null
        }
      >
        <ul className="space-y-1">
          {CHECKLIST.map((c) => (
            <li key={c.key} className="text-xs text-slate-800 flex gap-2 items-center">
              <input
                type="checkbox"
                checked={!!checked[c.key]}
                onChange={(e) => {
                  const nextChecked = { ...checked, [c.key]: e.target.checked };
                  setChecked(nextChecked);
                  persistPatch({ checked: { [c.key]: e.target.checked } });
                }}
                className="cursor-pointer"
              />
              <span className={checked[c.key] ? 'line-through text-slate-400' : ''}>{c.label}</span>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex justify-between items-baseline">
          <div className="text-[10px] text-slate-500">
            {Object.values(checked).filter(Boolean).length} of {CHECKLIST.length} complete
          </div>
          {prepState?.completed_at && (
            <div className="text-[10px] text-slate-500 italic">
              Completed {new Date(prepState.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function PostCall({ leadId, leadName, leadPhone, leadEmail, appointmentAt: initialApptAt }) {
  const firstName = (leadName || '').split(' ')[0] || 'there';
  const apptTimeStr = initialApptAt
    ? new Date(initialApptAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '[time]';

  const [thankYou, setThankYou] = React.useState(
    `Hey ${firstName}, great speaking with you. I'll have everything ready for our call at ${apptTimeStr}. Talk soon 👍`
  );
  const [emailSubject, setEmailSubject] = React.useState('Your Health Coverage Options');
  const [emailBody, setEmailBody] = React.useState(
    `Hey ${firstName},\n\nGreat speaking with you today. I'll go over your options during our call at ${apptTimeStr}.\n\nLooking forward to helping you find the best fit.`
  );
  const [emailTo, setEmailTo] = React.useState(leadEmail || '');
  const [notes, setNotes] = React.useState('');
  const [objections, setObjections] = React.useState('');
  const [budget, setBudget] = React.useState('');
  const [interest, setInterest] = React.useState('warm');
  const [apptAt, setApptAt] = React.useState(initialApptAt || '');
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const headers = await bearer();
      const objectionsList = objections.split(/[,\n]/).map(x => x.trim()).filter(Boolean);
      const body = {
        lead_id: leadId,
        appointment_at: apptAt || null,
        thank_you_text: thankYou,
        notes,
        objections: objectionsList,
        interest_level: interest,
      };
      const bcents = Number(budget);
      if (Number.isFinite(bcents) && bcents > 0) body.budget_monthly_cents = Math.round(bcents * 100);
      if (emailTo && emailSubject) {
        body.recap_email = { to_email: emailTo, subject: emailSubject, body_text: emailBody };
      }
      const r = await fetch('/api/call-command/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      setResult(r.ok ? { ok: true, ...j } : { ok: false, error: j.error || `HTTP ${r.status}` });
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card title="Post-call sequence">
        <div className="text-[11px] text-slate-600 mb-2">
          Runs six steps in order: thank-you SMS → recap email → CRM notes → appointment → reminder schedule → post-call stamp. Any single-step failure is flagged individually.
        </div>
      </Card>

      <Card title="1 · Thank-you text (immediate)">
        <textarea
          value={thankYou}
          onChange={(e) => setThankYou(e.target.value)}
          rows={3}
          className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200"
          disabled={!leadPhone}
        />
        {!leadPhone && <div className="text-[10px] text-red-700 mt-1">No phone on file — will be skipped.</div>}
      </Card>

      <Card title="2 · Recap email">
        <div className="space-y-2">
          <input
            type="email"
            placeholder="to_email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200"
          />
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200"
          />
          <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            rows={4}
            className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200"
          />
          <div className="text-[10px] text-slate-500">Leave email blank to skip step 2.</div>
        </div>
      </Card>

      <Card title="3 · CRM notes">
        <textarea
          placeholder="Family details, needs, budget hints, anything the next call needs to know…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200"
        />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input
            type="text"
            placeholder="objections (comma-sep)"
            value={objections}
            onChange={(e) => setObjections(e.target.value)}
            className="text-xs bg-slate-50 rounded p-2 border border-slate-200"
          />
          <input
            type="number"
            placeholder="budget $/mo"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="text-xs bg-slate-50 rounded p-2 border border-slate-200"
          />
        </div>
        <select
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
          className="mt-2 text-xs bg-slate-50 rounded p-2 border border-slate-200 w-full"
        >
          <option value="cold">cold</option>
          <option value="warm">warm</option>
          <option value="hot">hot</option>
        </select>
      </Card>

      <Card title="4 · Appointment + reminders">
        <input
          type="datetime-local"
          value={apptAt ? apptAt.slice(0, 16) : ''}
          onChange={(e) => setApptAt(e.target.value ? new Date(e.target.value).toISOString() : '')}
          className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200"
        />
        <div className="text-[10px] text-slate-500 mt-2">
          Auto-schedules: halfway (midpoint) · night before (appt − 14h) · morning of (appt − 3h)
        </div>
      </Card>

      <button
        onClick={run}
        disabled={running}
        className={`w-full py-3 rounded-md font-bold text-sm ${
          running ? 'bg-slate-300 text-slate-500' : 'bg-emerald-600 text-white hover:bg-emerald-700'
        }`}
      >
        {running ? 'Running sequence…' : 'Log call — run all 6 steps'}
      </button>

      {result && (
        <Card title="Result">
          {result.ok ? (
            <div>
              <div className="text-xs font-bold text-emerald-800 mb-2">Sequence complete</div>
              <ul className="space-y-0.5 text-[11px]">
                {Object.entries(result.actions_taken || {}).map(([k, v]) => (
                  <li key={k} className={v ? 'text-emerald-700' : 'text-red-700'}>
                    {v ? '✓' : '✗'} {k.replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
              {(result.warnings || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Warnings</div>
                  <ul className="space-y-0.5 text-[10px] text-amber-800">
                    {result.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-red-700">Failed: {result.error}</div>
          )}
        </Card>
      )}
    </div>
  );
}

const OUTREACH_TEMPLATES = [
  { key: 'set_1',       label: 'Set 1 — Ashley opener' },
  { key: 'set_2',       label: 'Set 2 — Stacy PPO opener' },
  { key: 'set_3',       label: 'Set 3 — Self-employed long form' },
  { key: 'set_4',       label: 'Set 4 — Rotating-name health team' },
  { key: 'follow_up_1', label: 'Follow-up 1 — quick rate check' },
  { key: 'follow_up_2', label: 'Follow-up 2 — savings hook' },
];

function Outreach() {
  const [templateKey, setTemplateKey] = React.useState('set_2');
  const [variants, setVariants] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(null);

  const preview = async () => {
    setLoading(true);
    setVariants([]);
    try {
      const headers = await bearer();
      const r = await fetch('/api/self-gen/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ template_key: templateKey, count: 3 }),
      });
      const j = await r.json();
      if (r.ok) setVariants(j.variants || []);
    } catch { /* soft-fail */ }
    finally { setLoading(false); }
  };

  const copyToClipboard = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(idx);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="space-y-3">
      <Card title="Self-gen outreach templates">
        <div className="text-[11px] text-slate-600 mb-2">
          Health-sector cold outreach + follow-ups with spintax variation. Preview 3 variants and pick the one you like — send via the existing SMS flow.
        </div>
        <select
          value={templateKey}
          onChange={(e) => setTemplateKey(e.target.value)}
          className="w-full text-xs bg-slate-50 rounded p-2 border border-slate-200 mb-2"
        >
          {OUTREACH_TEMPLATES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <button
          onClick={preview}
          disabled={loading}
          className={`w-full py-2 rounded-md font-bold text-xs ${
            loading ? 'bg-slate-300 text-slate-500' : 'bg-slate-900 text-white hover:bg-slate-700'
          }`}
        >
          {loading ? 'Generating…' : 'Preview 3 variants'}
        </button>
      </Card>

      {variants.map((v, i) => (
        <Card key={i} title={`Variant ${i + 1}`} right={
          <button
            onClick={() => copyToClipboard(v.text, i)}
            className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
              copied === i ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-900 text-white hover:bg-slate-700'
            }`}
            disabled={!v.text}
          >
            {copied === i ? 'Copied ✓' : 'Copy'}
          </button>
        }>
          {v.text ? (
            <>
              <div className="text-xs text-slate-800 bg-slate-50 rounded p-2 whitespace-pre-wrap">{v.text}</div>
              {v.warnings?.length > 0 && (
                <div className="mt-1 text-[10px] text-amber-800">{v.warnings.join(' · ')}</div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                {v.text.length} chars · seed {v.seed}
              </div>
            </>
          ) : (
            <div className="text-xs text-red-700">{v.warnings?.[0] || 'Failed to render'}</div>
          )}
        </Card>
      ))}
    </div>
  );
}

export default function CallCommandPanel({ leadId, leadName, leadPhone, leadEmail, appointmentAt = null, onClose = null }) {
  const [tab, setTab] = React.useState('pre');

  if (!leadId) {
    return (
      <div className="p-8 text-center text-slate-500">
        <div className="text-lg font-bold text-slate-700 mb-2">Call Command</div>
        <div className="text-sm">Open a lead first — Call Command needs a lead ID.</div>
      </div>
    );
  }

  return (
    <div className="max-w-none mx-auto p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Call Command</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">{leadName || 'Unnamed lead'}</h2>
        </div>
        {onClose && <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg">×</button>}
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[
          { key: 'pre',  label: 'Pre-call' },
          { key: 'post', label: 'Post-call' },
          { key: 'out',  label: 'Outreach' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs font-semibold ${
              tab === t.key
                ? 'text-slate-900 border-b-2 border-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pre'  && <PreCall  leadId={leadId} appointmentAt={appointmentAt} />}
      {tab === 'post' && <PostCall leadId={leadId} leadName={leadName} leadPhone={leadPhone} leadEmail={leadEmail} appointmentAt={appointmentAt} />}
      {tab === 'out'  && <Outreach />}
    </div>
  );
}
